import { SHIP, type Vec3 } from "@tiny-seas/shared";
import { RECIPES, SAMPLE_BANKS, type Bank, type FilterSpec, type Recipe, type RecipeArgs, type SampleLayer, type Synth } from "./audio/catalog.ts";
import type { Surface } from "./audio/gameplay.ts";
import { settings, type Settings } from "./settings.ts";

/**
 * Spatial game audio: CC0 recordings (public/audio) layered with Web Audio
 * synthesis, positioned with HRTF panners around a listener that follows the
 * camera, over ambience beds driven by the surroundings (open water, surf,
 * underwater, and every nearby ship's wake, rigging and flooding).
 *
 * The context is created on the first user gesture (browser autoplay policy);
 * recordings download in the background and each sound uses a procedural
 * stand-in until its recording is decoded, so nothing ever waits for audio.
 */

export type Sfx =
  | "step"
  | "land"
  | "swim"
  | "click"
  | "dig"
  | "treasure"
  | "pickup"
  | "drop"
  | "sale"
  | "impact"
  | "ram"
  | "ropeSnap"
  | "sailTear"
  | "wheelSmash"
  | "gunWreck"
  | "repair"
  | "repaired"
  | "bail"
  | "scoop"
  | "sinking"
  | "drowning"
  | "splash"
  | "anchor"
  | "anchorDrop"
  | "anchorSplash"
  | "anchorClick"
  | "sail"
  | "milestone"
  | "leak"
  | "death"
  | "cannon"
  | "cannonFar"
  | "ram"
  | "ballWhiz"
  | "hitConfirm"
  | "ballHit"
  | "ballSplash"
  | "ballLand"
  | "playerHit"
  | "swordSwing"
  | "swordFinisher"
  | "swordHit"
  | "swordHitHeavy"
  | "swordBlock"
  | "hullCreak"
  | "swordParry"
  | "slide"
  | "spyglass"
  | "restock"
  | "bell"
  | "lantern"
  | "pistolShot"
  | "pistolReload"
  | "pistolEmpty"
  | "danger";

export interface SoundOptions {
  /** World position; omitted for centred personal/UI cues. */
  position?: Readonly<Vec3>;
  surface?: Surface;
  /** 0 … 1, default 1. */
  intensity?: number;
}

export interface ShipAudioState {
  slot: number;
  generation: number;
  x: number;
  y: number;
  z: number;
  speed: number;
  sail: number;
  anchored: boolean;
  flooding: number;
  /** Server leak bitmask. */
  leaks: number;
  sinking: boolean;
  sinkIn: number;
}

export interface AmbienceFrame {
  underwater: boolean;
  /** 0 … 1 proximity to the nearest coastline. */
  shore: number;
  ships: readonly ShipAudioState[];
}

const MAX_VOICES = 48;
const MAX_INTERRUPTED = 4;
const MAX_RETIRING_SHIPS = 4;
const RELEASE = 0.02;
const FETCH_CONCURRENCY = 4;
const NOISE_SECONDS = 2;
const LOWPASS_OPEN = 18_000;
const LOWPASS_UNDERWATER = 700;
const DUCK_LEVEL = 0.55;
const DUCK_HOLD = 0.25;

interface Voice {
  priority: number;
  out: GainNode;
  nodes: AudioNode[];
  sources: AudioScheduledSourceNode[];
  live: number;
  interrupted: boolean;
  done: boolean;
}

interface Beds {
  nodes: AudioNode[];
  sources: AudioScheduledSourceNode[];
  open: GainNode;
  surf: GainNode;
  swells: [GainNode, GainNode];
  under: GainNode;
}

interface ShipBed {
  slot: number;
  generation: number;
  out: GainNode;
  panner: PannerNode;
  wake: GainNode;
  wakeFilter: BiquadFilterNode;
  air: GainNode;
  flood: GainNode;
  nodes: AudioNode[];
  sources: AudioScheduledSourceNode[];
  nextCreak: number;
  seen: boolean;
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

function popcount(n: number): number {
  let c = 0;
  for (; n; n >>= 1) c += n & 1;
  return c;
}

/** Holds a param at its current value from `t`, where supported, so a new ramp starts without a jump. */
function hold(p: AudioParam, t: number): void {
  // Firefox lacks cancelAndHoldAtTime.
  if (typeof p.cancelAndHoldAtTime === "function") p.cancelAndHoldAtTime(t);
  else p.cancelScheduledValues(t);
}

/** Recipe primitives bound to the voice being built (one reusable instance). */
class EventSynth implements Synth {
  voice: Voice | null = null;
  t = 0;
  rate = 1;

  constructor(private readonly engine: AudioEngine) {}

  sample(bank: Bank, layer: SampleLayer = {}): boolean {
    const voice = this.voice;
    const ctx = this.engine.context;
    const buffer = this.engine.pick(bank);
    if (!voice || !ctx || !buffer) return false;
    const rate = (layer.rate ?? 1) * this.rate;
    const src = new AudioBufferSourceNode(ctx, { buffer, playbackRate: rate });
    const g = new GainNode(ctx, { gain: layer.gain ?? 1 });
    const start = this.t + (layer.delay ?? 0);
    let head: AudioNode = src;
    if (layer.filter) {
      const f = filterNode(ctx, layer.filter);
      src.connect(f);
      voice.nodes.push(f);
      head = f;
    }
    head.connect(g);
    g.connect(voice.out);
    voice.nodes.push(g);
    const dur = layer.duration;
    if (dur === undefined) src.start(start, typeof layer.offset === "number" ? layer.offset : 0);
    else {
      // Offsets and lengths are in buffer time; the envelope is in output time.
      const span = Math.min(buffer.duration, dur * rate);
      const offset = layer.offset === "random" ? Math.random() * Math.max(0, buffer.duration - span) : (layer.offset ?? 0);
      const peak = layer.gain ?? 1;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(peak, start + 0.01);
      g.gain.setValueAtTime(peak, start + Math.max(0.01, dur - 0.04));
      g.gain.linearRampToValueAtTime(0, start + dur);
      src.start(start, offset, span);
    }
    this.engine.track(voice, src);
    return true;
  }

  tone(freq: number, duration: number, gain: number, type: OscillatorType, endFreq?: number, delay = 0): void {
    const voice = this.voice;
    const ctx = this.engine.context;
    if (!voice || !ctx) return;
    const t = this.t + delay;
    const o = new OscillatorNode(ctx, { type, frequency: freq * this.rate });
    if (endFreq) o.frequency.exponentialRampToValueAtTime(endFreq * this.rate, t + duration);
    const g = new GainNode(ctx, { gain: 0 });
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + duration);
    o.connect(g);
    g.connect(voice.out);
    voice.nodes.push(g);
    o.start(t);
    o.stop(t + duration + 0.05);
    this.engine.track(voice, o);
  }

  noise(duration: number, filter: FilterSpec, gain: number, color: "white" | "low", delay = 0, attack = 0.003): void {
    const voice = this.voice;
    const ctx = this.engine.context;
    const buffer = this.engine.noiseBuffer(color);
    if (!voice || !ctx || !buffer) return;
    const t = this.t + delay;
    const src = new AudioBufferSourceNode(ctx, { buffer });
    const f = filterNode(ctx, filter);
    const g = new GainNode(ctx, { gain: 0 });
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.001, t + Math.max(duration, attack + 0.01));
    src.connect(f);
    f.connect(g);
    g.connect(voice.out);
    voice.nodes.push(f, g);
    src.start(t, Math.random() * Math.max(0, NOISE_SECONDS - duration - 0.1));
    src.stop(t + duration + 0.05);
    this.engine.track(voice, src);
  }
}

function filterNode(ctx: BaseAudioContext, f: FilterSpec): BiquadFilterNode {
  return new BiquadFilterNode(ctx, { type: f.type, frequency: f.freq, Q: f.q ?? 0.707 });
}

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private worldFx: GainNode | null = null;
  private uiFx: GainNode | null = null;
  private amb: GainNode | null = null;
  private duck: GainNode | null = null;
  private worldLowpass: BiquadFilterNode | null = null;
  private outputNodes: AudioNode[] = [];
  private white: AudioBuffer | null = null;
  private low: AudioBuffer | null = null;
  private failed = false;
  private disposed = false;
  private active = false;
  private unsubscribe: (() => void) | null = null;

  private readonly listener: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly forward: Vec3 = { x: 0, y: 0, z: 1 };
  private readonly up: Vec3 = { x: 0, y: 1, z: 0 };
  private readonly synth = new EventSynth(this);
  private voices: Voice[] = [];
  private interrupted = 0;

  private loading: Promise<void> | null = null;
  private readonly bytes = new Map<string, ArrayBuffer>();
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly failedSamples = new Set<string>();
  private readonly lastVariant = new Map<Bank, number>();

  private beds: Beds | null = null;
  private readonly ships = new Map<number, ShipBed>();
  private retiringShips: ShipBed[] = [];
  private readonly targets = new Map<AudioParam, number>();
  private underwater = false;
  private clocksStale = true;
  private readonly surfNext: [number, number] = [0, 0];
  private bubbleNext = 0;
  private duckUntil = 0;
  private ducked = false;

  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Gameplay is active, the page visible and the context producing sound. */
  get running(): boolean {
    return this.active && !!this.ctx && this.ctx.state === "running" && document.visibilityState === "visible";
  }

  /** Starts downloading the recordings (once). Never rejects; failures fall back to synthesis. */
  preload(): Promise<void> {
    this.loading ??= this.fetchAll();
    return this.loading;
  }

  /** Call from a user gesture handler. Safe to call repeatedly. */
  unlock(): void {
    if (this.failed || this.disposed) return;
    void this.preload();
    if (this.ctx) {
      if (this.ctx.state === "suspended" && document.visibilityState === "visible") this.ctx.resume().catch(() => undefined);
      return;
    }
    try {
      this.build(new AudioContext({ latencyHint: "interactive" }));
    } catch (e) {
      this.failed = true;
      this.ctx = null;
      console.warn("[audio] unavailable; continuing without sound", e);
    }
  }

  /** In-game ambience and sounds run only while a game session is active (not in the lobby). */
  setGameplayActive(active: boolean): void {
    if (this.active === active || this.disposed) return;
    this.active = active;
    if (!this.ctx) return;
    if (active) this.startBeds();
    else {
      this.stopVoices();
      this.stopBeds();
    }
  }

  setListener(position: Readonly<Vec3>, forward: Readonly<Vec3>, up: Readonly<Vec3>): void {
    Object.assign(this.listener, { x: position.x, y: position.y, z: position.z });
    Object.assign(this.forward, { x: forward.x, y: forward.y, z: forward.z });
    Object.assign(this.up, { x: up.x, y: up.y, z: up.z });
    this.applyListener();
  }

  /** Plays a sound; positioned sounds are spatialised around the listener. */
  play(kind: Sfx, options: SoundOptions = {}): void {
    const recipe = RECIPES[kind];
    this.trigger(recipe, recipe.route === "ui" ? undefined : options.position, options.surface ?? "wood", options.intensity ?? 1);
  }

  /** Per render frame: ambience beds follow the listener's surroundings and nearby ships. */
  updateAmbience(frame: AmbienceFrame): void {
    const ctx = this.ctx;
    const beds = this.beds;
    if (!ctx || !beds || ctx.state !== "running") return;
    const now = ctx.currentTime;
    if (this.clocksStale) {
      this.clocksStale = false;
      this.surfNext[0] = now + Math.random() * 2;
      this.surfNext[1] = now + 1 + Math.random() * 3;
      this.bubbleNext = now;
      for (const bed of this.ships.values()) bed.nextCreak = now + 2 + Math.random() * 4;
    }
    if (frame.underwater !== this.underwater && this.worldLowpass) {
      this.underwater = frame.underwater;
      this.worldLowpass.frequency.setTargetAtTime(frame.underwater ? LOWPASS_UNDERWATER : LOWPASS_OPEN, now, 0.05);
    }
    const under = this.underwater;
    this.ramp(beds.open.gain, under ? 0.025 : 0.055, now, 0.13);
    this.ramp(beds.surf.gain, under ? 0 : 0.07 * clamp01(frame.shore), now, 0.13);
    this.ramp(beds.under.gain, under ? 0.1 : 0, now, 0.13);
    for (let i = 0; i < 2; i++) {
      if (now < this.surfNext[i]!) continue;
      const period = 5 + Math.random() * 4;
      const env = beds.swells[i]!.gain;
      hold(env, now);
      env.setTargetAtTime(1, now, period * 0.12);
      env.setTargetAtTime(0.2, now + period * 0.45, period * 0.15);
      this.surfNext[i] = now + period;
    }
    if (under && now >= this.bubbleNext) {
      this.bubble(ctx, beds.under, now);
      this.bubbleNext = now + 0.15 + Math.random() * 0.45;
    }
    if (this.ducked && now >= this.duckUntil && this.duck) {
      this.ducked = false;
      this.duck.gain.setTargetAtTime(1, now, 0.12);
    }
    this.updateShips(ctx, frame.ships, now);
  }

  /** Stops everything and releases the context (page unload). */
  dispose(): void {
    if (this.disposed) return;
    this.stopVoices();
    this.stopBeds();
    this.disposed = true;
    this.active = false;
    window.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("pagehide", this.onPageHide);
    window.removeEventListener("pageshow", this.onPageShow);
    this.unsubscribe?.();
    this.unsubscribe = null;
    for (const n of this.outputNodes) n.disconnect();
    this.outputNodes = [];
    void this.ctx?.close().catch(() => undefined);
    this.ctx = this.master = this.worldFx = this.uiFx = this.amb = this.duck = this.worldLowpass = null;
    this.buffers.clear();
    this.bytes.clear();
  }

  // ---------------------------------------------------------------------------
  // Graph

  private build(ctx: AudioContext): void {
    this.ctx = ctx;
    const compressor = new DynamicsCompressorNode(ctx, { threshold: -12, knee: 12, ratio: 4, attack: 0.003, release: 0.2 });
    const master = new GainNode(ctx, { gain: 0 });
    const lowpass = new BiquadFilterNode(ctx, { type: "lowpass", frequency: LOWPASS_OPEN, Q: 0.707 });
    const worldFx = new GainNode(ctx);
    const uiFx = new GainNode(ctx);
    const amb = new GainNode(ctx);
    const duck = new GainNode(ctx);
    worldFx.connect(lowpass);
    amb.connect(duck);
    duck.connect(lowpass);
    lowpass.connect(master);
    uiFx.connect(master);
    master.connect(compressor);
    compressor.connect(ctx.destination);
    this.outputNodes = [compressor, master, lowpass, worldFx, uiFx, amb, duck];
    Object.assign(this, { master, worldFx, uiFx, amb, duck, worldLowpass: lowpass });
    this.white = this.makeNoise(ctx, false);
    this.low = this.makeNoise(ctx, true);
    this.applyVolumes(settings.value);
    this.unsubscribe = settings.subscribe((s) => this.applyVolumes(s));
    window.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("pagehide", this.onPageHide);
    window.addEventListener("pageshow", this.onPageShow);
    this.applyListener();
    for (const path of [...this.bytes.keys()]) this.decode(path);
    if (this.active) this.startBeds();
  }

  private applyVolumes(s: Settings): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.worldFx || !this.uiFx || !this.amb) return;
    const t = ctx.currentTime;
    this.master.gain.setTargetAtTime(s.muted ? 0 : s.master, t, 0.05);
    this.worldFx.gain.setTargetAtTime(s.effects, t, 0.05);
    this.uiFx.gain.setTargetAtTime(s.effects, t, 0.05);
    this.amb.gain.setTargetAtTime(s.ambience, t, 0.05);
  }

  /** Game coordinates are left-handed (+Z forward); Web Audio is right-handed, so Z is mirrored. */
  private applyListener(): void {
    const l = this.ctx?.listener;
    if (!l) return;
    const p = this.listener, f = this.forward, u = this.up;
    // Firefox exposes only the legacy setter methods on the listener.
    if (l.positionX) {
      l.positionX.value = p.x;
      l.positionY.value = p.y;
      l.positionZ.value = -p.z;
      l.forwardX.value = f.x;
      l.forwardY.value = f.y;
      l.forwardZ.value = -f.z;
      l.upX.value = u.x;
      l.upY.value = u.y;
      l.upZ.value = -u.z;
    } else {
      l.setPosition(p.x, p.y, -p.z);
      l.setOrientation(f.x, f.y, -f.z, u.x, u.y, -u.z);
    }
  }

  /** Unit-RMS noise: white for air/grit, or one-pole lowpassed ("low") for rumble and water body. */
  private makeNoise(ctx: AudioContext, lowpassed: boolean): AudioBuffer {
    const len = ctx.sampleRate * NOISE_SECONDS;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let b = 0;
    let sum = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      b = lowpassed ? 0.97 * b + 0.03 * w : w;
      d[i] = b;
      sum += b * b;
    }
    const scale = 1 / Math.sqrt(sum / len || 1);
    for (let i = 0; i < len; i++) d[i]! *= scale;
    return buf;
  }

  noiseBuffer(color: "white" | "low"): AudioBuffer | null {
    return color === "white" ? this.white : this.low;
  }

  private ramp(p: AudioParam, target: number, now: number, tau: number): void {
    const last = this.targets.get(p);
    if (last !== undefined && Math.abs(last - target) < 0.002) return;
    this.targets.set(p, target);
    p.setTargetAtTime(target, now, tau);
  }

  // ---------------------------------------------------------------------------
  // Samples

  private async fetchAll(): Promise<void> {
    const queue: string[] = Object.values(SAMPLE_BANKS).flat();
    const worker = async (): Promise<void> => {
      for (let path = queue.shift(); path !== undefined; path = queue.shift()) await this.fetchSample(path);
    };
    await Promise.all(Array.from({ length: FETCH_CONCURRENCY }, worker));
  }

  private async fetchSample(path: string): Promise<void> {
    try {
      const res = await fetch(new URL(path, document.baseURI));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const bytes = await res.arrayBuffer();
      if (this.disposed) return;
      this.bytes.set(path, bytes);
      this.decode(path);
    } catch (e) {
      this.sampleFailed(path, e);
    }
  }

  /** Decodes once a context exists; bytes are dropped once handed to the decoder. */
  private decode(path: string): void {
    const ctx = this.ctx;
    const bytes = this.bytes.get(path);
    if (!ctx || !bytes) return;
    this.bytes.delete(path);
    ctx.decodeAudioData(bytes).then(
      (buffer) => {
        if (!this.disposed && this.ctx === ctx) this.buffers.set(path, buffer);
      },
      (e: unknown) => this.sampleFailed(path, e),
    );
  }

  private sampleFailed(path: string, e: unknown): void {
    if (this.failedSamples.has(path)) return;
    this.failedSamples.add(path);
    console.warn(`[audio] sample unavailable: ${path}`, e);
  }

  /** A decoded variant from the bank, never the same one twice in a row when there is a choice. */
  pick(bank: Bank): AudioBuffer | null {
    const paths = SAMPLE_BANKS[bank];
    const last = this.lastVariant.get(bank);
    let choice = -1;
    let seen = 0;
    for (let i = 0; i < paths.length; i++) {
      if (!this.buffers.has(paths[i]!) || (i === last && paths.length > 1 && this.readyCount(bank) > 1)) continue;
      // Reservoir sampling over the eligible variants.
      if (Math.random() * ++seen < 1) choice = i;
    }
    if (choice < 0) return null;
    this.lastVariant.set(bank, choice);
    return this.buffers.get(paths[choice]!) ?? null;
  }

  private readyCount(bank: Bank): number {
    let n = 0;
    for (const p of SAMPLE_BANKS[bank]) if (this.buffers.has(p)) n++;
    return n;
  }

  // ---------------------------------------------------------------------------
  // Voices

  private trigger(recipe: Recipe, position: Readonly<Vec3> | undefined, surface: Surface, intensity: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.active || ctx.state !== "running") return;
    const bus = recipe.route === "ui" ? this.uiFx : recipe.route === "ambience" ? this.amb : this.worldFx;
    if (!bus) return;
    if (position) {
      const l = this.listener;
      if (Math.hypot(position.x - l.x, position.y - l.y, position.z - l.z) > recipe.cull) return;
    }
    if (!this.admit(recipe.priority)) return;
    const stable = recipe.stable === true;
    const level = recipe.gain * (stable ? 1 : 1 + (Math.random() * 2 - 1) * 0.08);
    const out = new GainNode(ctx, { gain: level });
    const voice: Voice = { priority: recipe.priority, out, nodes: [out], sources: [], live: 0, interrupted: false, done: false };
    if (position) {
      const panner = new PannerNode(ctx, {
        panningModel: "HRTF",
        distanceModel: "inverse",
        refDistance: recipe.ref,
        rolloffFactor: 1,
        maxDistance: 10_000,
        positionX: position.x,
        positionY: position.y,
        positionZ: -position.z,
        channelCount: 1,
        channelCountMode: "explicit",
      });
      out.connect(panner);
      panner.connect(bus);
      voice.nodes.push(panner);
    } else out.connect(bus);
    const s = this.synth;
    s.voice = voice;
    s.t = ctx.currentTime;
    s.rate = stable ? 1 : 1 + (Math.random() * 2 - 1) * 0.05;
    const args: RecipeArgs = { surface, intensity: clamp01(intensity) };
    recipe.play(s, args);
    s.voice = null;
    if (voice.live === 0) {
      this.release(voice);
      return;
    }
    this.voices.push(voice);
    if (recipe.duck) this.duckAmbience(ctx.currentTime);
  }

  track(voice: Voice, src: AudioScheduledSourceNode): void {
    voice.live++;
    voice.sources.push(src);
    voice.nodes.push(src);
    src.onended = () => {
      if (--voice.live <= 0) this.release(voice);
    };
  }

  /** Room for a new voice of this priority, stealing the oldest strictly lower-priority one if full. */
  private admit(priority: number): boolean {
    if (this.voices.length - this.interrupted < MAX_VOICES) return true;
    if (this.interrupted >= MAX_INTERRUPTED) return false;
    const victim = this.voices.find((v) => !v.interrupted && v.priority < priority);
    if (!victim) return false;
    this.interrupt(victim);
    return true;
  }

  private interrupt(v: Voice): void {
    const ctx = this.ctx;
    if (!ctx || v.interrupted) return;
    v.interrupted = true;
    this.interrupted++;
    const now = ctx.currentTime;
    hold(v.out.gain, now);
    v.out.gain.linearRampToValueAtTime(0, now + RELEASE);
    for (const s of v.sources) {
      try {
        s.stop(now + RELEASE);
      } catch {
        // Already stopped.
      }
    }
  }

  private release(v: Voice): void {
    if (v.done) return;
    v.done = true;
    if (v.interrupted) this.interrupted--;
    const i = this.voices.indexOf(v);
    if (i >= 0) this.voices.splice(i, 1);
    for (const n of v.nodes) n.disconnect();
  }

  /** Immediately silences every one-shot (hidden page, leaving the game). */
  private stopVoices(): void {
    for (const v of this.voices) {
      for (const s of v.sources) {
        s.onended = null;
        try {
          s.stop();
        } catch {
          // Already stopped.
        }
      }
      v.done = true;
      for (const n of v.nodes) n.disconnect();
    }
    this.voices = [];
    this.interrupted = 0;
  }

  private duckAmbience(now: number): void {
    const duck = this.duck;
    if (!duck) return;
    this.duckUntil = Math.max(this.duckUntil, now + DUCK_HOLD);
    if (this.ducked) return;
    this.ducked = true;
    hold(duck.gain, now);
    duck.gain.setTargetAtTime(DUCK_LEVEL, now, 0.01);
  }

  // ---------------------------------------------------------------------------
  // Ambience beds

  private loop(ctx: AudioContext, color: "white" | "low", owner: { nodes: AudioNode[]; sources: AudioScheduledSourceNode[] }): AudioBufferSourceNode {
    const src = new AudioBufferSourceNode(ctx, { buffer: color === "white" ? this.white : this.low, loop: true });
    src.start(ctx.currentTime, Math.random() * NOISE_SECONDS);
    owner.sources.push(src);
    owner.nodes.push(src);
    return src;
  }

  /** Slow oscillator added onto `param` (depth in the param's units). */
  private lfo(ctx: AudioContext, param: AudioParam, freq: number, depth: number, owner: { nodes: AudioNode[]; sources: AudioScheduledSourceNode[] }): void {
    const o = new OscillatorNode(ctx, { frequency: freq });
    const g = new GainNode(ctx, { gain: depth });
    o.connect(g);
    g.connect(param);
    o.start(ctx.currentTime + Math.random() * 2);
    owner.sources.push(o);
    owner.nodes.push(o, g);
  }

  private startBeds(): void {
    const ctx = this.ctx;
    const amb = this.amb;
    if (!ctx || !amb || this.beds) return;
    const owner = { nodes: [] as AudioNode[], sources: [] as AudioScheduledSourceNode[] };
    const chain = (src: AudioNode, ...nodes: AudioNode[]): void => {
      let head = src;
      for (const n of nodes) {
        head.connect(n);
        head = n;
      }
      head.connect(amb);
      owner.nodes.push(...nodes);
    };
    // Open water: brown noise with a slow swell in level and tone.
    const openFilter = new BiquadFilterNode(ctx, { type: "lowpass", frequency: 500 });
    const swell = new GainNode(ctx, { gain: 0.75 });
    const open = new GainNode(ctx, { gain: 0 });
    chain(this.loop(ctx, "low", owner), openFilter, swell, open);
    this.lfo(ctx, openFilter.frequency, 0.07, 200, owner);
    this.lfo(ctx, swell.gain, 0.12, 0.25, owner);
    // Surf: two band-limited layers with independent wave envelopes.
    const surf = new GainNode(ctx, { gain: 0 });
    surf.connect(amb);
    owner.nodes.push(surf);
    const swells: GainNode[] = [];
    for (const [freq, q] of [[450, 0.6], [900, 0.8]] as const) {
      const f = new BiquadFilterNode(ctx, { type: "bandpass", frequency: freq, Q: q });
      const env = new GainNode(ctx, { gain: 0.2 });
      const src = this.loop(ctx, "white", owner);
      src.connect(f);
      f.connect(env);
      env.connect(surf);
      owner.nodes.push(f, env);
      swells.push(env);
    }
    // Underwater: muffled rumble (bubbles are scheduled into it).
    const under = new GainNode(ctx, { gain: 0 });
    chain(this.loop(ctx, "low", owner), new BiquadFilterNode(ctx, { type: "lowpass", frequency: 250 }), under);
    this.beds = { ...owner, open, surf, swells: [swells[0]!, swells[1]!], under };
    this.clocksStale = true;
  }

  private stopBeds(): void {
    const stop = (o: { nodes: AudioNode[]; sources: AudioScheduledSourceNode[] }): void => {
      for (const s of o.sources) {
        s.onended = null;
        try {
          s.stop();
        } catch {
          // Already stopped.
        }
      }
      for (const n of o.nodes) n.disconnect();
    };
    if (this.beds) stop(this.beds);
    this.beds = null;
    for (const b of this.ships.values()) stop(b);
    for (const b of this.retiringShips) stop(b);
    this.ships.clear();
    this.retiringShips = [];
    this.targets.clear();
    const ctx = this.ctx;
    if (ctx && this.worldLowpass && this.duck) {
      hold(this.worldLowpass.frequency, ctx.currentTime);
      this.worldLowpass.frequency.setValueAtTime(LOWPASS_OPEN, ctx.currentTime);
      hold(this.duck.gain, ctx.currentTime);
      this.duck.gain.setValueAtTime(1, ctx.currentTime);
    }
    this.underwater = false;
    this.ducked = false;
    this.duckUntil = 0;
  }

  private bubble(ctx: AudioContext, dst: AudioNode, now: number): void {
    const f = 250 + Math.random() * 350;
    const o = new OscillatorNode(ctx, { frequency: f });
    o.frequency.exponentialRampToValueAtTime(f * 2, now + 0.06);
    const g = new GainNode(ctx, { gain: 0 });
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.3 + Math.random() * 0.3, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
    o.connect(g);
    g.connect(dst);
    o.onended = () => {
      o.disconnect();
      g.disconnect();
    };
    o.start(now);
    o.stop(now + 0.1);
  }

  private updateShips(ctx: AudioContext, ships: readonly ShipAudioState[], now: number): void {
    for (const bed of this.ships.values()) bed.seen = false;
    for (const s of ships) {
      let bed = this.ships.get(s.slot);
      if (bed && bed.generation !== s.generation) {
        this.retireShip(bed, now);
        bed = undefined;
      }
      if (!bed) {
        bed = this.createShip(ctx, s, now) ?? undefined;
        if (!bed) continue;
        this.ships.set(s.slot, bed);
      }
      bed.seen = true;
      const p = bed.panner;
      p.positionX.value = s.x;
      p.positionY.value = s.y;
      p.positionZ.value = -s.z;
      const v = clamp01(Math.abs(s.speed) / 10);
      const sail = clamp01(s.sail / 3);
      const flooding = clamp01(s.flooding / 100);
      const sink = s.sinking ? clamp01(1 - s.sinkIn / SHIP.sinkSeconds) : 0;
      this.ramp(bed.wake.gain, 0.12 * v, now, 0.1);
      this.ramp(bed.wakeFilter.frequency, 400 + 600 * v, now, 0.1);
      this.ramp(bed.air.gain, 0.07 * sail * (0.3 + 0.7 * v), now, 0.1);
      this.ramp(bed.flood.gain, Math.min(0.2, 0.12 * flooding + 0.02 * popcount(s.leaks)) + 0.1 * sink, now, 0.1);
      if (now >= bed.nextCreak) {
        const strain = Math.max(v, flooding, sink);
        this.trigger(RECIPES.hullCreak, s, "wood", strain);
        const calm = 6 + Math.random() * 4;
        const rough = 2 + Math.random() * 2;
        bed.nextCreak = now + (calm + (rough - calm) * Math.max(v, flooding)) * (1 - 0.6 * sink);
      }
    }
    for (const [slot, bed] of this.ships) {
      if (bed.seen) continue;
      this.ships.delete(slot);
      this.retireShip(bed, now);
    }
  }

  private createShip(ctx: AudioContext, s: ShipAudioState, now: number): ShipBed | null {
    const amb = this.amb;
    if (!amb) return null;
    const owner = { nodes: [] as AudioNode[], sources: [] as AudioScheduledSourceNode[] };
    const panner = new PannerNode(ctx, {
      panningModel: "HRTF",
      distanceModel: "inverse",
      refDistance: 10,
      rolloffFactor: 1,
      maxDistance: 10_000,
      positionX: s.x,
      positionY: s.y,
      positionZ: -s.z,
      channelCount: 1,
      channelCountMode: "explicit",
    });
    const out = new GainNode(ctx, { gain: 0 });
    out.gain.setTargetAtTime(1, now, 0.1);
    out.connect(panner);
    panner.connect(amb);
    owner.nodes.push(out, panner);
    const layer = (color: "white" | "low", filter: BiquadFilterNode): GainNode => {
      const g = new GainNode(ctx, { gain: 0 });
      this.loop(ctx, color, owner).connect(filter);
      filter.connect(g);
      g.connect(out);
      owner.nodes.push(filter, g);
      return g;
    };
    const wakeFilter = new BiquadFilterNode(ctx, { type: "bandpass", frequency: 400, Q: 0.7 });
    const wake = layer("low", wakeFilter);
    const airFilter = new BiquadFilterNode(ctx, { type: "bandpass", frequency: 1800, Q: 0.5 });
    const air = layer("white", airFilter);
    this.lfo(ctx, airFilter.frequency, 0.4, 400, owner);
    const floodFilter = new BiquadFilterNode(ctx, { type: "bandpass", frequency: 350, Q: 1 });
    const flood = layer("low", floodFilter);
    this.lfo(ctx, floodFilter.frequency, 0.8, 150, owner);
    return { ...owner, slot: s.slot, generation: s.generation, out, panner, wake, wakeFilter, air, flood, nextCreak: now + 2 + Math.random() * 4, seen: true };
  }

  /** Fades a ship bed out; at most a few fade at once, the oldest is cut if more arrive. */
  private retireShip(bed: ShipBed, now: number): void {
    hold(bed.out.gain, now);
    for (const p of [bed.wake.gain, bed.wakeFilter.frequency, bed.air.gain, bed.flood.gain]) this.targets.delete(p);
    bed.out.gain.setTargetAtTime(0, now, 0.1);
    let live = bed.sources.length;
    for (const src of bed.sources) {
      src.onended = () => {
        if (--live > 0) return;
        for (const n of bed.nodes) n.disconnect();
        const i = this.retiringShips.indexOf(bed);
        if (i >= 0) this.retiringShips.splice(i, 1);
      };
      try {
        src.stop(now + 0.35);
      } catch {
        // Already stopped.
      }
    }
    this.retiringShips.push(bed);
    while (this.retiringShips.length > MAX_RETIRING_SHIPS) {
      const oldest = this.retiringShips.shift()!;
      for (const src of oldest.sources) {
        src.onended = null;
        try {
          src.stop();
        } catch {
          // Already stopped.
        }
      }
      for (const n of oldest.nodes) n.disconnect();
    }
  }

  // ---------------------------------------------------------------------------
  // Page lifecycle

  private readonly onVisibility = (): void => {
    const ctx = this.ctx;
    if (!ctx || this.disposed) return;
    this.clocksStale = true;
    if (document.visibilityState === "hidden") {
      this.stopVoices();
      ctx.suspend().catch(() => undefined);
    } else ctx.resume().catch(() => undefined);
  };

  private readonly onPageHide = (e: PageTransitionEvent): void => {
    if (!e.persisted) {
      this.dispose();
      return;
    }
    this.stopVoices();
    this.ctx?.suspend().catch(() => undefined);
  };

  private readonly onPageShow = (e: PageTransitionEvent): void => {
    if (!e.persisted || !this.ctx || this.disposed || document.visibilityState !== "visible") return;
    this.clocksStale = true;
    this.ctx.resume().catch(() => undefined);
  };
}

export const audio = new AudioEngine();
